import { applyTransaction, getEntityType, OrArray } from '@datorama/akita';
import { ErrorHandlerService } from '@y42/error-handling';
import { UpdateModel } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { ImpexChildService } from '@y42/impex';
import { JobQuery } from '@y42/job';
import { Y42FeatureType } from '@y42/json-schema';
import { Optional } from '@y42/typescript';
import { ensureArray } from '@y42/util/ensure-array';
import { getSafeString } from '@y42/util/safe-string';
import {
  areSettingsEqual,
  DatasourceQuery,
  DatasourceStore,
  DatasourceStoreModel,
  DatasourceTableGitFactoryService,
  DatasourceTableQuery,
  DatasourceTableSettingsBase,
  DatasourceTableSettingsGitModel,
  DatasourceTablesPatch,
  DatasourceTableState,
  DatasourceTableStore,
  DatasourceTableStoreModel,
  DatasourceUIModel,
} from '../state';
import { DatasourceTableSettingsCheckService } from './datasource-table-settings-check.service';

interface NewDatasourceTableModel {
  name: string;
  $name: string;
  settings?: Optional<DatasourceTableSettingsGitModel, '$schema'>;
}

type DatasourceTableUpdateModel = UpdateModel<DatasourceTableState, DatasourceTableStoreModel>;

export abstract class DatasourceTableService extends ImpexChildService<
  DatasourceTableState,
  DatasourceTableStoreModel
> {
  constructor(
    readonly cfg: {
      feature: Y42FeatureType;
      parentStore: DatasourceStore;
      parentQuery: DatasourceQuery;
      query: DatasourceTableQuery;
      store: DatasourceTableStore;
      jobQuery: JobQuery;
      gitFactory: DatasourceTableGitFactoryService;
      errorHandlerService: ErrorHandlerService;
      datasourceTableSettingsCheckService: DatasourceTableSettingsCheckService;
    },
  ) {
    super({
      feature: cfg.feature,
      gitFactory: cfg.gitFactory,
      parentStore: cfg.parentStore,
      parentQuery: cfg.parentQuery,
      store: cfg.store,
      query: cfg.query,
    });
  }

  async createFor(
    datasource: DatasourceStoreModel,
    name: string,
    settings?: Optional<DatasourceTableSettingsGitModel, '$schema'>,
  ): Promise<DatasourceTableStoreModel> {
    const datasourceName = datasource.$name;

    const createdTable = applyTransaction(() => {
      const childFreeTable = this.cfg.store.create({
        ...new Y42MetadataGitModel(),
        $schema: METADATA_JSON_SCHEMA_URL,
        $parent: datasource.$path,
        $name: name,
        name,
        settings: {
          ...(settings ?? {}),
          $schema: settings?.$schema || `${datasource.settings.$schema.split('/').slice(0, -1).join('/')}/table.json`,
        },
        $parentFolders: [datasourceName],
      });

      const table = {
        ...childFreeTable,
        $children: this.cfg.jobQuery
          .getAll()
          .filter((job) => job.path === childFreeTable.$path)
          .map((job) => job.request_id),
      };

      this.cfg.store.upsertMany([table]);

      this.cfg.parentStore.update(datasource.$path, (st) => {
        const children = [...(st.$children || []), table.$path];
        return { $children: children } as Partial<getEntityType<DatasourceTableState>>;
      });
      return table;
    });

    await Promise.all([this.cfg.parentStore, this.cfg.store.pending]);

    return createdTable;
  }

  async createForMany(
    datasource: DatasourceStoreModel,
    datasourceTables: OrArray<NewDatasourceTableModel>,
  ): Promise<DatasourceTableStoreModel[]> {
    const datasourceTablesArray = ensureArray(datasourceTables);
    if (datasourceTablesArray.length === 0) {
      return [];
    }
    const createdTables = applyTransaction(() => {
      const datasourceName = datasource.$name;
      const allJobs = this.cfg.jobQuery.getAll();
      const childFreeTables = this.cfg.store.createMany(
        datasourceTablesArray.map(({ $name, name, settings }) => ({
          ...new Y42MetadataGitModel(),
          $schema: METADATA_JSON_SCHEMA_URL,
          $parent: datasource.$path,
          $name,
          $level: 1,
          name,
          settings: {
            ...(settings ?? {}),
            $schema: settings?.$schema || `${datasource.settings.$schema.split('/').slice(0, -1).join('/')}/table.json`,
          },
          $parentFolders: [datasourceName],
        })),
      );

      const tables = childFreeTables.map((table) => {
        return {
          ...table,
          $children: allJobs.filter((job) => job.path === table.$path).map((job) => job.request_id),
        };
      });

      this.cfg.store.upsertMany(tables);

      this.cfg.parentStore.update(datasource.$path, (st) => {
        const children = [...(st.$children || []), ...tables.map((table) => table.$path)];
        return { $children: children } as Partial<getEntityType<DatasourceTableState>>;
      });

      return tables;
    });

    await Promise.all([this.cfg.store.pending, this.cfg.parentStore.pending]);

    return createdTables;
  }

  async create(
    name: string,
    settings?: Optional<DatasourceTableSettingsGitModel, '$schema'>,
  ): Promise<DatasourceTableStoreModel> {
    const activeDatasource = this.cfg.parentQuery.getActive()!;
    return this.createFor(activeDatasource, name, settings);
  }

  async createMany(datasourceTables: OrArray<NewDatasourceTableModel>): Promise<DatasourceTableStoreModel[]> {
    if (Array.isArray(datasourceTables) && datasourceTables.length === 0) {
      return [];
    }

    const activeDatasource = this.cfg.parentQuery.getActive()!;
    return this.createForMany(activeDatasource, datasourceTables);
  }

  async update(table: Partial<DatasourceTableStoreModel>, $path: DatasourceTableStoreModel['$path']) {
    this.cfg.store.update($path, table);
    await this.cfg.store.pending;
  }

  async updateMany(items: DatasourceTableUpdateModel[]) {
    if (items.length === 0) {
      return;
    }

    applyTransaction(() => {
      items.forEach(({ $path, item }) => {
        this.cfg.store.update($path, item);
      });
    });

    await this.cfg.store.pending;
  }

  async delete($paths: OrArray<DatasourceTableStoreModel['$path']>) {
    $paths = ensureArray($paths);

    if ($paths.length === 0) {
      return;
    }

    const promises = [this.cfg.store.pending];
    applyTransaction(() => {
      const firstPath = $paths[0];
      if (!firstPath) {
        return;
      }

      const datasourcePath = this.cfg.query.getEntity(firstPath)?.$parent;
      if (datasourcePath) {
        const pathsToDelete = new Set($paths);
        this.cfg.parentStore.update(
          datasourcePath,
          (st) =>
            ({ $children: st.$children.filter((child) => !pathsToDelete.has(child)) } satisfies Partial<
              getEntityType<DatasourceTableState>
            >),
        );
        promises.push(this.cfg.parentStore.pending);
      }

      this.cfg.store.delete($paths);
    });

    await Promise.all(promises);
  }

  getOneDiff<TKey extends string>(
    newTableName: string,
    newTableSetting: DatasourceTableSettingsGitModel,
    existingTable: DatasourceTableStoreModel,
  ): DatasourceTablesPatch {
    const sharedKey = newTableName as unknown as TKey;
    const newTables = new Map<TKey, string>([[sharedKey, newTableName]]);
    const newTableSettings = new Map<TKey, DatasourceTableSettingsGitModel>([[sharedKey, newTableSetting]]);
    const existingTables = new Map<TKey, DatasourceTableStoreModel>([[sharedKey, existingTable]]);

    return this.getDiff(newTables, newTableSettings, existingTables);
  }

  getDiff<TKey extends string>(
    newAndExistingTableNames: Map<TKey, string>,
    newAndExistingTableSettings: Map<TKey, DatasourceTableSettingsGitModel>,
    currentTables: Map<TKey, DatasourceTableStoreModel>,
  ): DatasourceTablesPatch {
    const diff: DatasourceTablesPatch = {
      newTables: [],
      updatedTables: [],
      deletedTables: [],
    };

    newAndExistingTableSettings.forEach((settings: DatasourceTableSettingsGitModel, key: TKey) => {
      const name = newAndExistingTableNames.get(key) || 'n/a';
      const safeKey = getSafeString(key);
      const existingTableFromKey = currentTables.get(key);
      const existingTableFromSafeKey = currentTables.get(safeKey as TKey);
      const existingTable = existingTableFromKey ?? existingTableFromSafeKey;
      if (existingTable) {
        if (!existingTable.settings || !areSettingsEqual(settings, existingTable.settings)) {
          diff.updatedTables.push({
            $path: existingTable.$path,
            settings,
          });
        }
      } else {
        diff.newTables.push({ name, $name: safeKey, settings });
      }
    });

    currentTables.forEach((table, key) => {
      if (!newAndExistingTableNames.get(key)) {
        diff.deletedTables.push({ $path: table.$path });
      }
    });

    return diff;
  }

  async patch(patch: DatasourceTablesPatch): Promise<void> {
    if (!patch.newTables.length && !patch.updatedTables.length && !patch.deletedTables.length) {
      return;
    }

    await Promise.all([
      this.createMany(patch.newTables),
      this.updateMany(
        patch.updatedTables.map((table): { item: Partial<DatasourceTableStoreModel>; $path: string } => ({
          item: { settings: table.settings },
          $path: table.$path,
        })),
      ),
      this.delete(patch.deletedTables.map((table) => table.$path)),
    ]);
  }

  async hasSettings(table: DatasourceTableStoreModel) {
    return this.cfg.datasourceTableSettingsCheckService.checkIfHasSettings(table.settings);
  }

  async editSettings(datasource: DatasourceUIModel, table: DatasourceTableStoreModel) {
    if (!table.settings) {
      this.cfg.errorHandlerService.error('[DatasourceTableService.editImportSettings]: Table settings are undefined');
      return;
    }

    const newSettings = await this.cfg.datasourceTableSettingsCheckService.checkDatasourceTable(
      table.name,
      table.settings,
      datasource,
    );

    if (!newSettings) {
      return;
    }

    const patch = this.getOneDiff(table.name, newSettings, table);

    return this.patch(patch);
  }

  checkDatasourceTable<T extends DatasourceTableSettingsBase = DatasourceTableSettingsBase>(
    tableName: string,
    tableSettings: T,
    datasource: DatasourceUIModel,
  ): Promise<T> {
    return this.cfg.datasourceTableSettingsCheckService.checkDatasourceTable(tableName, tableSettings, datasource);
  }

  checkDatasourceTables<T extends DatasourceTableSettingsBase = DatasourceTableSettingsBase>(
    tableSettings: Map<string, T>,
    datasource: DatasourceUIModel,
  ): Promise<Map<string, T>> {
    return this.cfg.datasourceTableSettingsCheckService.checkDatasourceTables(tableSettings, datasource);
  }
}
